// client/src/components/marketer/project/modals/ScheduleModal.js
import React, { useEffect, useMemo, useState } from 'react';
import { API_URL } from '../constants';
import AlertModal from './AlertModal';
import '../../../../styles/marketer/modals/ScheduleModal.css';

function toISODate(v) {
  if (!v) return '';
  const d = new Date(v);
  if (isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth()+1).padStart(2,'0');
  const dd = String(d.getDate()).padStart(2,'0');
  return `${y}-${m}-${dd}`;
}
function addDaysStr(baseStr, n) {
  if (!baseStr) return '';
  const d = new Date(baseStr);
  d.setDate(d.getDate() + n);
  return toISODate(d);
}

export default function ScheduleModal({ open, onClose, projectId, perReward, initialSchedule, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [recruitEnd, setRecruitEnd] = useState('');
  const [submitEnd, setSubmitEnd] = useState('');
  const [dates, setDates] = useState([]); // settlement dates string[]
  const [alertMessage, setAlertMessage] = useState('');
  const cnt = Number(perReward?.settlementCount || 0);
  const usePer = !!perReward?.use;

  // 초기 로딩/열기 시 값 세팅
  useEffect(() => {
    if (open) {
      setSaving(false);
      setRecruitEnd(initialSchedule?.recruitEnd ? toISODate(initialSchedule.recruitEnd) : '');
      setSubmitEnd(initialSchedule?.submitEnd ? toISODate(initialSchedule.submitEnd) : '');
      const initDates = Array.isArray(perReward?.settlementDates) ? perReward.settlementDates.map(toISODate) : [];
      setDates(initDates);
    }
  }, [open, initialSchedule, perReward]);

  // 훅들은 조건 없이 최상단에서 호출
  const today = toISODate(new Date());
  const minRecruit = useMemo(() => addDaysStr(today, 2), [today]);
  const minSubmit = useMemo(() => (recruitEnd ? addDaysStr(recruitEnd, 7) : ''), [recruitEnd]);
  const firstSettlementMin = useMemo(() => (submitEnd ? addDaysStr(submitEnd, 10) : ''), [submitEnd]);

  // 결산일 배열 길이 유지
  useEffect(() => {
    if (!open) return;
    if (!usePer || cnt <= 0) { setDates([]); return; }
    setDates(prev => {
      const next = [...prev];
      if (next.length < cnt) next.push(...Array.from({ length: cnt - next.length }, () => ''));
      if (next.length > cnt) next.length = cnt;
      return next;
    });
  }, [cnt, usePer, open]);

  if (!open) return null;

  const validate = () => {
    if (!recruitEnd) return '모집/선정 마감일을 선택해주세요.';
    if (recruitEnd < minRecruit) return '모집/선정 마감일은 오늘로부터 최소 2일 이후여야 합니다.';
    if (!submitEnd) return '결과물 제출 마감일을 선택해주세요.';
    if (submitEnd < minSubmit) return '결과물 제출 마감일은 모집/선정 마감일로부터 최소 7일 이후여야 합니다.';
    if (usePer && cnt > 0) {
      if (dates.length !== cnt) return '결산일 개수가 맞지 않습니다.';
      for (let i=0; i<dates.length; i++) {
        const d = dates[i];
        if (!d) return `${i+1}회 결산일을 입력해주세요.`;
        const minForThis = i === 0 ? firstSettlementMin : addDaysStr(dates[i-1], 10);
        if (d < minForThis) return `${i+1}회 결산일은 이전 회차로부터 최소 10일 이후여야 합니다.`;
      }
    }
    return '';
  };

  const save = async () => {
    const err = validate();
    if (err) { setAlertMessage(err); return; }
    setSaving(true);
    try {
      const payload = {
        recruitEnd,
        submitEnd,
        settlementDates: (usePer && cnt > 0) ? dates : []
      };
      const res = await fetch(`${API_URL}/api/marketer/mybiz/projects/${encodeURIComponent(projectId)}/schedule`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const j = await res.json().catch(()=>({}));
      if (!res.ok || !j?.ok) throw new Error(j?.message || '저장 실패');

      onSaved({
        recruitEnd: j?.schedule?.recruitEnd,
        submitEnd: j?.schedule?.submitEnd,
        settlementDates: j?.perPurchaseReward?.settlementDates || []
      });
      onClose();
    } catch (e) { setAlertMessage(e.message || '저장 실패'); }
    setSaving(false);
  };

  return (
    <div className="schedule-modal mbm-overlay">
      <div className="schedule-modal__content mbm">
        <button onClick={onClose} aria-label="close" className="schedule-modal__close mbm-close">×</button>

        <div className="schedule-modal__header">
          <div className="schedule-modal__icon">📅</div>
          <div className="schedule-modal__title mbm-title">프로젝트 일정을 설정해주세요</div>
          <div className="schedule-modal__description">모집, 제출, 결산 일정을 순서대로 입력해주세요</div>
        </div>

        <div className="schedule-modal__form">
          <div className="schedule-modal__field">
            <label className="schedule-modal__label">
              📌 모집 및 선정 기간 마감일
            </label>
            <input
              type="date"
              min={minRecruit || undefined}
              value={recruitEnd}
              onChange={e=>{ setRecruitEnd(e.target.value); if (submitEnd && e.target.value) { const minS = addDaysStr(e.target.value, 7); if (submitEnd < minS) setSubmitEnd(''); } }}
              className="schedule-modal__input"
            />
            <div className="schedule-modal__hint">
              💡 마감일 이후 선정이 불가능합니다. 최소 <strong>{minRecruit}</strong> 이후 선택
            </div>
          </div>

          <div className="schedule-modal__field">
            <label className="schedule-modal__label">
              📤 결과물 제출 마감일
            </label>
            <input
              type="date"
              disabled={!recruitEnd}
              min={minSubmit || undefined}
              value={submitEnd}
              onChange={e=>{ setSubmitEnd(e.target.value); if (usePer && cnt>0) { setDates(Array.from({length:cnt}, ()=>'')); } }}
              className="schedule-modal__input"
            />
            <div className="schedule-modal__hint">
              💡 마감일 이후 결과물 제출이 불가하며, 광고주는 제출 마감일 7일 후까지 승인 처리해주세요
            </div>
          </div>

          {usePer && cnt > 0 && (
            <div className="schedule-modal__settlement-section">
              <div className="schedule-modal__settlement-header">
                <div className="schedule-modal__settlement-icon">💰</div>
                <div className="schedule-modal__settlement-title">CPS 리워드 결산일 설정</div>
                <div className="schedule-modal__settlement-badge">총 {cnt}회 결산</div>
              </div>

              <div className="schedule-modal__settlement-grid">
                {Array.from({ length: cnt }).map((_, i) => {
                  const min = i === 0 ? firstSettlementMin : addDaysStr(dates[i-1] || firstSettlementMin, 10);
                  return (
                    <div key={i} className="schedule-modal__settlement-row">
                      <div className="schedule-modal__settlement-label">
                        <span className="schedule-modal__settlement-number">{i+1}</span>
                        {i+1}회 결산일
                      </div>
                      <input
                        type="date"
                        disabled={!submitEnd}
                        min={min || undefined}
                        value={dates[i] || ''}
                        onChange={e => {
                          const v = e.target.value;
                          setDates(prev => {
                            const next = [...prev];
                            next[i] = v;
                            // 이후 회차가 이전보다 빠르면 비워줌
                            for (let k=i+1; k<next.length; k++) {
                              const prevMin = addDaysStr(next[k-1] || firstSettlementMin, 10);
                              if (next[k] && next[k] < prevMin) next[k] = '';
                            }
                            return next;
                          });
                        }}
                        className="schedule-modal__input"
                      />
                    </div>
                  );
                })}
              </div>

              <div className="schedule-modal__info-box">
                <div className="schedule-modal__info-title">⚠️ 결산일 설정 안내</div>
                <ul className="schedule-modal__info-list">
                  <li>1회차 결산일은 결과물 제출 마감일 10일 이후부터 설정 가능합니다</li>
                  <li>각 회차는 이전 회차로부터 최소 10일 이후 날짜여야 하며 총 {cnt}개를 모두 입력해야 합니다</li>
                  <li>결산일로부터 7일 내 미결산 시 개인당 최대 추가리워드 예산이 소진될 수 있습니다</li>
                </ul>
              </div>
            </div>
          )}
        </div>

        <div className="schedule-modal__actions mbm-foot">
          <button type="button" onClick={save} disabled={saving} className="schedule-modal__button schedule-modal__button--submit mb-btn mb-btn-primary">
            {saving ? '저장 중...' : '확 인'}
          </button>
        </div>
      </div>

      <AlertModal 
        open={!!alertMessage} 
        onClose={() => setAlertMessage('')} 
        message={alertMessage} 
      />
    </div>
  );
}